import { CSSProperties, RefObject, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";

/**
 * Measures the space a screen has left, as two custom properties for its stylesheet to spend:
 *
 * - `--pif-done-fit`: from the element's top edge to the bottom of the viewport, so a short screen
 *   can fill it and centre inside it without asking for more than it has. The stylesheet's `100vh`
 *   fallback is never the right answer on any current route — every PostIF screen, the emailed
 *   /sign/:token one included since Epic 79820, starts below the fixed header and the page band, so
 *   a viewport-tall box overflows by all of that and scrolls for nothing.
 * - `--pif-done-footer`: the fixed portal footer's height, which the screen keeps as padding so its
 *   last row can't sit underneath it once the content is tall enough for the page to scroll (the
 *   page's own 15px bottom padding is shorter than the footer, so it can't clear it on its own).
 *
 * Measured rather than derived from the layout tokens because these insets move — an impersonation
 * banner, a taller footer, mobile chrome — and a hard-coded sum then reserves the wrong amount.
 *
 * The first measurement can still be too generous: any inset this doesn't know about, or a
 * fractional viewport at browser zoom, leaves the page scrolling by that difference. So a second
 * pass reads the overflow the browser actually ended up with and hands back that much of the
 * reservation, never going below what the content itself needs — see the correction effect below.
 */
const useFitToViewport = <T extends HTMLElement>(): [RefObject<T>, CSSProperties] => {
  const ref = useRef<T>(null);
  const [fit, setFit] = useState<number>();
  const [footer, setFooter] = useState(0);

  const measure = useCallback(() => {
    const el = ref.current;
    if (!el) return;

    // Document offset, so it reads the same whether or not the page is already scrolled. Safe from
    // feeding back into itself: this is the last box in flow, so its own height can't move its top.
    const top = el.getBoundingClientRect().top + window.scrollY;
    const content = el.closest<HTMLElement>(".page-content");
    const padding = content ? parseFloat(window.getComputedStyle(content).paddingBottom) || 0 : 0;

    // The document's client height, not `innerHeight`: that one counts the strip a horizontal
    // scrollbar occupies as usable, so on any width that has one the screen reserves ~15px more
    // than it can show and the page gains a vertical scrollbar for it.
    const viewport = document.documentElement.clientHeight || window.innerHeight;

    setFooter(document.querySelector<HTMLElement>(".footer")?.offsetHeight ?? 0);
    setFit(Math.max(0, Math.floor(viewport - top - padding)));
  }, []);

  // Before paint, so the content is never briefly laid out against the fallback and then jumps.
  useLayoutEffect(measure, [measure]);

  useEffect(() => {
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [measure]);

  useEffect(() => {
    if (fit === undefined) return;
    const el = ref.current;
    if (!el) return;

    const doc = document.documentElement;
    const overflow = doc.scrollHeight - doc.clientHeight;
    if (overflow <= 0) return;

    // Only give back space the screen is holding but not using. `slack` is what's left of the box
    // once its content and padding are accounted for, so when the content is genuinely taller than
    // the viewport this is zero, nothing shrinks, and the page scrolls as it should.
    const inner = el.firstElementChild as HTMLElement | null;
    const style = window.getComputedStyle(el);
    const used = (inner?.offsetHeight ?? 0) + parseFloat(style.paddingTop) + parseFloat(style.paddingBottom);
    const shrink = Math.min(overflow, Math.floor(el.offsetHeight - used));

    // Each pass strictly reduces the reservation, so this settles after one or two runs.
    if (shrink > 0) setFit(fit - shrink);
  }, [fit]);

  const style = useMemo<CSSProperties>(
    () =>
      fit === undefined
        ? {}
        : ({ "--pif-done-fit": `${fit}px`, "--pif-done-footer": `${footer}px` } as CSSProperties),
    [fit, footer],
  );

  return [ref, style];
};

export default useFitToViewport;
